import { z } from "zod";
import { FEE_HEADS } from "./fee-heads.js";
import { COMPONENT_TOKEN_MAX, isComponentToken } from "./head-lines.js";

/**
 * "Apply towards" — the optional instruction on `POST`/`PATCH
 * /fees/payments` naming which head a payment settles.
 *
 * ONE schema, imported by the API route and the web Record Payment form so
 * neither can accept something the other refuses — the same arrangement
 * `school-profile.ts` uses for the signup form. `note` is required, trimmed
 * and non-empty for `OTHER` (a directed payment against "Other" with no
 * explanation is unauditable) and simply ignored for every other head — the
 * schema doesn't reject an accidental note on TUITION, it just isn't read.
 *
 * `component` (2026-10-05, defect 7 — "IIT Fee is included in Tuition") narrows a
 * directed payment from the CATEGORY to one named fee LINE the student owes: the
 * `token` a line carries in `GET /fees/students/:id/head-breakdown` (`lines[]`) —
 * "c:IIT Fee" for a line the category fallback used to swallow, or the category
 * itself ("TUITION") for the category's own line when a custom sibling shares it.
 * `feeHead` stays the accounting category (the books are unchanged); the server
 * checks the line really belongs to it. Omit it and the request is exactly what
 * it always was.
 */
export const ApplyToSchema = z
  .object({
    feeHead: z.enum(FEE_HEADS),
    note: z.string().trim().max(200).optional(),
    component: z
      .string()
      .trim()
      .min(1)
      .max(COMPONENT_TOKEN_MAX)
      .refine((v) => isComponentToken(v), { message: "That fee line isn't recognised — pick it from the list." })
      .optional(),
  })
  .superRefine((val, ctx) => {
    if (val.feeHead === "OTHER") {
      const note = val.note?.trim() ?? "";
      if (note.length < 3) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["note"],
          message: "Say what this payment is for — a note is required when applying towards \"Other\".",
        });
      }
    }
  });

export type ApplyTo = z.infer<typeof ApplyToSchema>;
